import {
  BACKEND_URL,
  AUTH0_CAREPLAN_LOGOUT_URL,
  AUTH0_CAREPLAN_REDIRECT_FLAG,
  AUTH0_SESSION_PROBED_FLAG,
  QCS_HANDOFF_SESSION_FLAG,
  QCS_LOGIN_URL,
} from '../../../configs'
import { logout as logoutAction } from '../../authorization/actions/security'
import { markQcsHandoffLogout } from '../helpers/qcsHandoffSession'

export function signUpInvitedUser(user) {
  const headers = new Headers()
  headers.append('Content-Type', 'application/json;charset=UTF-8')
  return fetch(BACKEND_URL + '/invite/user', {
    method: 'POST',
    headers: headers,
    credentials: 'include',
    body: JSON.stringify(user),
  })
}

export function signUpUser(user) {
  const headers = new Headers()
  headers.append('Content-Type', 'application/json;charset=UTF-8')
  return fetch(BACKEND_URL + '/signUp/user', {
    method: 'POST',
    headers: headers,
    credentials: 'include',
    body: JSON.stringify(user),
  })
}

export function requestPasswordReset(email) {
  const headers = new Headers()
  headers.append('Content-Type', 'application/json;charset=UTF-8')
  return fetch(BACKEND_URL + '/resetPassword/requestReset', {
    method: 'POST',
    headers: headers,
    credentials: 'include',
    body: JSON.stringify({ email }),
  })
}

export function validateResetPasswordToken(token) {
  const headers = new Headers()
  headers.append('Content-Type', 'application/json;charset=UTF-8')
  return fetch(BACKEND_URL + '/resetPassword/validateToken', {
    method: 'POST',
    headers: headers,
    credentials: 'include',
    body: JSON.stringify({ token }),
  })
}

export function changeAdminPasswordWithResetToken(token, newPassword) {
  const headers = new Headers()
  headers.append('Content-Type', 'application/json;charset=UTF-8')
  return fetch(BACKEND_URL + '/resetPassword/changePassword', {
    method: 'POST',
    headers: headers,
    credentials: 'include',
    body: JSON.stringify({ token, newPassword }),
  })
}

export function getBotInviteInfo(code) {
  return fetch(BACKEND_URL + '/invite/' + code, {
    method: 'GET',
    credentials: 'include',
  })
}

export function login(user) {
  const headers = new Headers()

  // QCS supervision handoff: JSON body with handoff id only
  if (user && user.qcsHandoffId) {
    headers.append('Content-Type', 'application/json;charset=UTF-8')
    return fetch(BACKEND_URL + '/login', {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({ qcsHandoffId: user.qcsHandoffId }),
      headers: headers,
    })
  }

  // Standard email/password login (unchanged)
  headers.append('Content-Type', 'application/x-www-form-urlencoded')
  return fetch(BACKEND_URL + '/login', {
    method: 'POST',
    credentials: 'include',
    body: new URLSearchParams({
      'email': user.username,
      'password': user.password
    }),
    headers: headers,
  })
}

export function logout() {
  return fetch(BACKEND_URL + '/logout', {
    method: 'POST',
    credentials: 'include'
  }).then(() => logoutAction())
}

// Every localStorage key the admin panel writes. Deployed, the admin panel shares its
// origin (and so localStorage) with other portals, so logout must not clear() it all.
const ADMIN_LOCAL_STORAGE_KEYS = [
  'authenticated',
  'activeBotName',
  'sortDirection',
  'sortBy',
  'loggedIn',
  'qcsOrganisationsView',
  'carebrainOrgPackages',
  'carebrainPackageUiFlags',
  'qcsDevUsageStub',
  'qcsDevUsageState',
  'qcsDevAudits',
  'qcsDevBotIds',
]
const ADMIN_LOCAL_STORAGE_PREFIXES = ['widget-demo-preview-']

const clearAdminLocalStorage = () => {
  const keys = []
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i)
    if (key && (ADMIN_LOCAL_STORAGE_KEYS.includes(key) || ADMIN_LOCAL_STORAGE_PREFIXES.some(p => key.startsWith(p)))) {
      keys.push(key)
    }
  }
  keys.forEach(key => localStorage.removeItem(key))
}

// Admin-owned localStorage is wiped; session keys mirror the security reducer's clearClientSession.
const clearClientSessionStorage = () => {
  try {
    clearAdminLocalStorage()
    localStorage.setItem('authenticated', 'false')
  } catch {
    // ignore — still navigate away
  }
  try {
    sessionStorage.removeItem('support_active_user')
    sessionStorage.removeItem(AUTH0_CAREPLAN_REDIRECT_FLAG)
    sessionStorage.removeItem(AUTH0_SESSION_PROBED_FLAG)
    sessionStorage.removeItem(QCS_HANDOFF_SESSION_FLAG)
  } catch {
    // ignore — still navigate away
  }
}

/**
 * Auth0 users leave through the backend Auth0 logout. Must not dispatch the
 * Redux logout first — PrivateRoute would re-render and replace this
 * navigation with the silent authorize URL, signing the user straight back in.
 */
export function logoutAuth0User() {
  clearClientSessionStorage()
  window.location.assign(AUTH0_CAREPLAN_LOGOUT_URL)
}

/**
 * Auth0 handoff users: the Auth0 logout also signs them out of the QCS portal.
 * It returns to the admin panel, where index.js sends this tab on to QCS.
 */
export function logoutAuth0QcsHandoffUser() {
  clearClientSessionStorage()
  markQcsHandoffLogout()
  window.location.assign(AUTH0_CAREPLAN_LOGOUT_URL)
}

/**
 * Handoff users go back to QCS. Like logoutAuth0User, no Redux logout first —
 * PrivateRoute would replace this navigation with the Auth0 authorize URL.
 */
export function logoutQcsHandoffUser() {
  clearClientSessionStorage()
  return fetch(BACKEND_URL + '/logout', {
    method: 'POST',
    credentials: 'include'
  })
    .catch(() => null)
    .then(() => window.location.assign(QCS_LOGIN_URL))
}

/**
 * Expired handoff session: the backend already dropped it, so no POST /logout
 * (it would end sessions of other portals sharing the cookie).
 */
export function expireQcsHandoffSession() {
  clearClientSessionStorage()
  window.location.assign(QCS_LOGIN_URL)
}
